import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEO_ISSUES, type SeoIssue, type SeoIssueItemDto } from '@ecom/contracts';
import { CheckIcon, SearchIcon } from '@/components/icons';
import { ListCard, LoadMore, Page, Table, TableScroll, Td, Th, Tr } from '@/components/page';
import { Alert, ButtonLink, CardHeader, EmptyState, Skeleton, cx } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { useSeoHealthStore } from '@/stores/seo-health.store';

/** How bad each issue is for being found: a failure, or worth fixing. */
const SEVERITY: Record<SeoIssue, 'bad' | 'warn'> = {
  'product-no-description': 'warn',
  'product-thin-description': 'warn',
  'product-no-alt': 'warn',
  'product-duplicate-title': 'bad',
  'product-hidden': 'warn',
  'category-no-description': 'warn',
  'category-hidden': 'warn',
  'brand-no-description': 'warn',
};

/** Where an item is fixed. */
function editorOf(item: SeoIssueItemDto): string {
  if (item.kind === 'product') return `/products/${item.id}`;
  if (item.kind === 'category') return `/categories?edit=${item.id}`;
  return `/brands?edit=${item.id}`;
}

/**
 * SEO health (ADR-095): what search engines would find missing across the
 * catalogue — counted on the server, each issue a list of items to open and
 * fix. The store's own settings for titles, patterns and robots are in
 * Settings → SEO.
 */
export function SeoHealthPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { health, loading, error, issue, items, nextCursor, itemsLoading, load, open, more } = useSeoHealthStore();

  useEffect(() => {
    void load();
  }, [load]);

  const total = health ? SEO_ISSUES.reduce((sum, key) => sum + health.counts[key], 0) : 0;
  const settings = (
    <ButtonLink to="/settings/seo" variant="secondary">
      {t('settingsSeo')}
    </ButtonLink>
  );

  return (
    <Page title={t('seoHealthPage')} subtitle={t('seoHealthPageHint')} actions={settings} fill>
      {error ? <Alert>{error}</Alert> : null}
      {loading && !health ? (
        <Skeleton className="h-40" />
      ) : health ? (
        <>
          <p className="text-sm text-ink-muted">
            {t('seoHealthChecked', {
              products: count(health.totals.products, locale),
              categories: count(health.totals.categories, locale),
              brands: count(health.totals.brands, locale),
            })}
          </p>
          {total === 0 ? <Alert tone="success">{t('seoHealthAllGood')}</Alert> : null}
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {SEO_ISSUES.map((key) => {
              const number = health.counts[key];
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => void open(key)}
                  disabled={number === 0}
                  className={cx(
                    'flex flex-col items-start gap-1 rounded-card border bg-surface p-4 text-start transition-colors',
                    issue === key ? 'border-brand-500 ring-1 ring-brand-500' : 'border-line hover:border-brand-300',
                    number === 0 && 'cursor-default opacity-70',
                  )}
                >
                  <span
                    data-numeric
                    className={cx('text-2xl font-bold', number === 0 ? 'text-success' : SEVERITY[key] === 'bad' ? 'text-danger' : 'text-warning')}
                  >
                    {number === 0 ? <CheckIcon className="size-6" /> : count(number, locale)}
                  </span>
                  <span className="text-sm font-medium text-ink">{t(`seoIssue_${key}`)}</span>
                  <span className="text-xs text-ink-muted">{t(`seoIssueHint_${key}`)}</span>
                </button>
              );
            })}
          </div>
        </>
      ) : null}

      {issue ? (
        <ListCard>
          <CardHeader title={t(`seoIssue_${issue}`)} subtitle={t(`seoIssueHint_${issue}`)} />
          {itemsLoading && items.length === 0 ? (
            <Skeleton className="m-4 h-24" />
          ) : items.length === 0 ? (
            <EmptyState title={t('seoHealthAllGood')} icon={<SearchIcon />} />
          ) : (
            <TableScroll>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('seoItem')}</Th>
                    <Th>{t('seoItemDetail')}</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <Tr key={`${item.kind}-${item.id}`}>
                      <Td>
                        <span className="block font-medium text-ink">{item.name}</span>
                        <span dir="ltr" className="block text-start text-xs text-ink-muted">
                          {t(`seoKind_${item.kind}`)} · /{item.kind === 'product' ? 'products' : item.kind === 'category' ? 'categories' : 'brands'}/{item.slug}
                        </span>
                      </Td>
                      <Td className="text-sm text-ink-muted">{item.detail ? t(`seoDetail_${issue}`, { value: item.detail }) : '—'}</Td>
                      <Td align="end">
                        <Link to={editorOf(item)} className="text-sm font-medium text-brand-700 hover:underline">
                          {t('seoFix')}
                        </Link>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableScroll>
          )}
          <LoadMore visible={Boolean(nextCursor)} loading={itemsLoading} onClick={() => void more()} />
        </ListCard>
      ) : null}
    </Page>
  );
}
